import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from "sonner";
import { rdoFlags, getFlag } from '@/config/rdoFlags'; // Feature flags para migração gradual - próximos passos
// Fase 1: a normalização para o schema unificado não é mais aplicada ao estado
// da página (ver load abaixo). Conversão ocorre nos adapters por módulo.
import type { RDO as UnifiedRDO } from "@/domain/rdo/schema";
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { WorkforceCardsLayout } from '@/components/rdo/WorkforceCardsLayout';
import { EquipmentCardsLayout } from '@/components/rdo/EquipmentCardsLayout';
import { EditEquipmentModal } from '@/components/rdo/EditEquipmentModal';
import { WorkforceIndividualEditModal } from '@/components/rdo/WorkforceIndividualEditModal';
import { WorkforceTeamEditModal } from '@/components/rdo/WorkforceTeamEditModal';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useViewModePersistence } from '@/hooks/useViewModePersistence';
import { WeatherIconButton } from '@/components/rdo/WeatherIconButton';
import { DialogAddWorkforce } from '@/components/rdo/DialogAddWorkforce';

import { DialogAddEquipment } from '@/components/rdo/DialogAddEquipment';
import { DialogAddMaterial } from '@/components/rdo/DialogAddMaterial';
import { DialogFotos } from '@/components/rdo/DialogFotos';
import { DialogVideos } from '@/components/rdo/DialogVideos';
import { DialogAnexos } from '@/components/rdo/DialogAnexos';
import { MaterialsSection } from '@/components/rdo/MaterialsSection';
import { CommentsSection } from '@/components/rdo/CommentsSection';
import { PhotosSection } from '@/components/rdo/PhotosSection';
import { VideosSection } from '@/components/rdo/VideosSection';
import { AttachmentsSection } from '@/components/rdo/AttachmentsSection';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { WeatherCondition } from '@/utils/weatherUtils';
import { 
  ArrowLeft, 
  Save, 
  Check,
  Plus,
  Trash2,
  Cloud,
  Users,
  Timer,
  FileText,
  Sun,
  MessageSquare,
  Camera,
  Video,
  Paperclip,
  Upload,
  Image,
  FileVideo,
  X,
  Truck,
  LayoutGrid,
  List,
  ChevronLeft,
  ChevronRight,
  Edit,
  Sparkles
} from 'lucide-react';
import { getWeatherIconPlaceholder, getWeatherIconColor } from '@/utils/weatherUtils';

const WeatherIconHeader = ({ condition, label }: { condition?: string, label: string }) => {
  const IconComponent = getWeatherIconPlaceholder(condition || 'sun');
  const iconColor = getWeatherIconColor(condition || 'sun');
  
  console.log('WeatherIconHeader re-rendering:', { condition, label, iconColor });
  
  return (
    <h4 className="font-semibold flex items-center gap-2 text-sm">
      <IconComponent className={`w-4 h-4 ${iconColor}`} />
      {label}
    </h4>
  );
};
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getLastRDO, createRDO, updateRDO, listRDOs, getRDOByObraDate } from '@/services/rdoStorageSupabase';
import { useObraReportDefaults } from '@/hooks/useObraReportDefaults';
import { getObraById } from '@/services/obraStorageService';
import { formatYmd } from '@/utils/dateUtils';
import { useTasks } from '@/hooks/useTasks';
import { syncActivitiesToTasks } from '@/services/rdoTaskSync';
import type { RDO } from '@/services/rdoStorageService';


function PreenchendoRDO() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const obraId = searchParams.get('obraId');
  const dateStr = searchParams.get('date');
  const copyLastRDOParam = searchParams.get('copyLastRDO') === 'true';
  const rdoId = searchParams.get('rdoId');
  
  // Schema unificado para dados do RDO (Etapa 1 - Feature Flag: useUnifiedSchema)
  const [rdoData, setRdoData] = useState<any>({
    weather: { 
      morning: { condition: '', workable: false, observation: '' },
      afternoon: { condition: '', workable: false, observation: '' }
    },
    workforce: [],
    equipment: [],
    activities: [],
    occurrences: [],
    materials: { received: [], used: [] },
    comments: [],
    photos: [],
    videos: [],
    attachments: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasSaved, setHasSaved] = useState(false);
  // Fase 3: id do RDO já persistido nesta sessão (ou rascunho reaberto), para
  // evitar criar registros duplicados ao salvar/atualizar/finalizar.
  const [savedRdoId, setSavedRdoId] = useState<string | null>(null);
  // Nº do relatório (RDO-ANO-NNN) — carregado do Supabase (assíncrono).
  const [reportNumber, setReportNumber] = useState('');
  useEffect(() => {
    if (!obraId) { setReportNumber(''); return; }
    let active = true;
    listRDOs(obraId)
      .then((list) => {
        if (active) setReportNumber(`RDO-${new Date().getFullYear()}-${(list.length + 1).toString().padStart(3, '0')}`);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [obraId]);
  const [confirmExitOpen, setConfirmExitOpen] = useState(false);
  const initialDataRef = useRef<Partial<RDO> | null>(null);
  
  // Modal states
  const [showWorkforceModal, setShowWorkforceModal] = useState(false);
  const [showEquipmentModal, setShowEquipmentModal] = useState(false);
  const [showEditEquipmentModal, setShowEditEquipmentModal] = useState(false);
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [showActivityPhotoModal, setShowActivityPhotoModal] = useState(false);
  const [showActivityGalleryModal, setShowActivityGalleryModal] = useState(false);
  const [selectedActivityIndex, setSelectedActivityIndex] = useState<number | null>(null);
  const [selectedActivityFiles, setSelectedActivityFiles] = useState<FileList | null>(null);
  const [showOccurrencePhotoModal, setShowOccurrencePhotoModal] = useState(false);
  const [newCommentText, setNewCommentText] = useState("");
  const [selectedOccurrenceIndex, setSelectedOccurrenceIndex] = useState<number | null>(null);
  const [selectedOccurrenceFiles, setSelectedOccurrenceFiles] = useState<FileList | null>(null);
  const [showOccurrenceGalleryModal, setShowOccurrenceGalleryModal] = useState(false);
  const [editingEquipmentIndex, setEditingEquipmentIndex] = useState<number | null>(null);
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showCaptionEditModal, setShowCaptionEditModal] = useState(false);
  const [editingCaptionIndex, setEditingCaptionIndex] = useState<number | null>(null);
  const [editingCaptionType, setEditingCaptionType] = useState<'photo' | 'video' | null>(null);
  const [editingCaptionText, setEditingCaptionText] = useState("");
  const [videoViewerOpen, setVideoViewerOpen] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { getPreference, updatePreference } = useViewModePersistence();
  const [workforceViewMode, setWorkforceViewMode] = useState<'cards' | 'table'>(() => 
    getPreference('workforce', 'cards')
  );
  const [editingWorkforceIndex, setEditingWorkforceIndex] = useState<number | null>(null);
  
  // Estados para controlar modais de edição específicos
  const [showIndividualEditModal, setShowIndividualEditModal] = useState(false);
  const [showTeamEditModal, setShowTeamEditModal] = useState(false);
  const [editingWorkforceData, setEditingWorkforceData] = useState<any>(null);

  // Funções para edição e exclusão de mão de obra
  const handleEditWorkforce = (index: number) => {
    console.log('handleEditWorkforce called with index:', index);
    
    if (!rdoData.workforce || index >= rdoData.workforce.length) {
      console.warn('Invalid workforce index:', index);
      return;
    }
    
    const workforceItem = rdoData.workforce[index];
    console.log('Editing workforce item:', workforceItem);
    
    setEditingWorkforceIndex(index);
    setEditingWorkforceData(workforceItem);
    
    // Determinar qual modal abrir baseado na estrutura do item
    // Items de equipe têm teamId, teamName, totalMembers, members
    if ('teamId' in workforceItem && 'teamName' in workforceItem && 'members' in workforceItem) {
      // Item de equipe consolidada
      setShowTeamEditModal(true);
    } else {
      // Item individual (pessoa ou profissão isolada)  
      setShowIndividualEditModal(true);
    }
  };

  const handleDeleteWorkforce = (index: number) => {
    if (!rdoData.workforce) return;
    
    const updatedWorkforce = rdoData.workforce.filter((_, i) => i !== index);
    setRdoData(prev => ({
      ...prev,
      workforce: updatedWorkforce
    }));
  };

  const handleDeleteEquipment = (index: number) => {
    if (!rdoData.equipment) return;
    
    const updatedEquipment = rdoData.equipment.filter((_, i) => i !== index);
    setRdoData(prev => ({
      ...prev,
      equipment: updatedEquipment
    }));
    toast({
      title: "Equipamento removido",
      description: "Equipamento removido com sucesso"
    });
  };

  const handleEditEquipment = (index: number) => {
    setEditingEquipmentIndex(index);
    setShowEditEquipmentModal(true);
  };

  const handleSaveEquipmentEdit = (updatedEquipment: any) => {
    if (editingEquipmentIndex === null || !rdoData.equipment) return;

    const updatedEquipmentList = [...rdoData.equipment];
    const currentEquipment = updatedEquipmentList[editingEquipmentIndex];
    
    // Update only the fields that exist and are editable
    updatedEquipmentList[editingEquipmentIndex] = {
      ...currentEquipment,
      ...(updatedEquipment.quantity && { quantity: updatedEquipment.quantity }),
      ...(updatedEquipment.status && { ownershipType: updatedEquipment.status }),
      ...(updatedEquipment.leaseExpiryDate && { leaseExpiryDate: updatedEquipment.leaseExpiryDate })
    };

    setRdoData(prev => ({
      ...prev,
      equipment: updatedEquipmentList
    }));

    toast({
      title: "Equipamento atualizado",
      description: "Alterações salvas com sucesso"
    });

    setEditingEquipmentIndex(null);
  };

  const handleDeleteEquipmentFromEdit = () => {
    if (editingEquipmentIndex !== null) {
      handleDeleteEquipment(editingEquipmentIndex);
      setEditingEquipmentIndex(null);
    }
  };

  
  const { reportDefaults, isLoading: isLoadingDefaults } = useObraReportDefaults(obraId || '');
  // Tarefas reais desta obra — para vincular atividades do RDO (integração
  // com o Gestor de Tarefas). Filtradas por work_tags contendo a obra.
  const { tasks: obraTasks } = useTasks(obraId || undefined);
  const obra = getObraById(obraId || '');
  
  // Safe exit function with fallback navigation
  function safeExit() {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    // fallback se não houver histórico
    if (obraId && dateStr) {
      navigate(`/rdo?obra=${obraId}&date=${dateStr}`);
    } else if (obraId) {
      navigate(`/rdo?obra=${obraId}`);
    } else {
      navigate('/'); // ajuste para a rota que fizer sentido no seu app
    }
  }
  
  useEffect(() => {
    if (!obraId || !dateStr) {
      setError('Parâmetros obrigatórios (obraId e date) não fornecidos');
      setIsLoading(false);
      return;
    }
    
    const loadRDOData = async () => {
      try {
        setIsLoading(true);
        
        let initialData: Partial<RDO>;
        
        if (copyLastRDOParam) {
          const lastRDO = await getLastRDO(obraId);
          if (lastRDO) {
            // Copy only allowed sections - matching RDO.tsx structure exactly
            initialData = {
              obraId,
              date: dateStr,
              weather: lastRDO.weather || { 
                morning: { condition: '', workable: false, observation: '' },
                afternoon: { condition: '', workable: false, observation: '' }
              },
              workforce: lastRDO.workforce || [],
              equipment: lastRDO.equipment || [],
              activities: lastRDO.activities || [],
              // Don't copy: occurrences, materials, comments, photos, videos, attachments - start empty
              occurrences: [],
              materials: { received: [], used: [] },
              comments: [],
              photos: [],
              videos: [],
              attachments: [],
              status: 'draft'
            };
            toast({
              title: "RDO copiado",
              description: "Dados do último RDO foram copiados com sucesso."
            });
          } else {
            toast({
              title: "Aviso",
              description: "Não foi possível copiar o último RDO - nenhum RDO anterior encontrado.",
              variant: "destructive"
            });
            initialData = {
              obraId,
              date: dateStr,
              weather: { 
                morning: { condition: '', workable: false, observation: '' },
                afternoon: { condition: '', workable: false, observation: '' }
              },
              workforce: [],
              equipment: [],
              activities: [],
              occurrences: [],
              materials: { received: [], used: [] },
              comments: [],
              photos: [],
              videos: [],
              attachments: [],
              status: 'draft'
            };
          }
        } else {
          // Fase 3: reabrir rascunho existente para a mesma obra+data, em vez de
          // começar vazio (evita duplicar registros e sobrescrever com dados em
          // branco). Se já houver relatório finalizado/aprovado, redireciona para
          // a tela de visualização/edição (RDO.tsx).
          const existing = await getRDOByObraDate(obraId, dateStr);
          if (existing && existing.status && existing.status !== 'draft') {
            navigate(`/rdo?obra=${obraId}&date=${dateStr}`, { replace: true });
            return;
          }
          if (existing) {
            initialData = { ...existing };
            setSavedRdoId(existing.id);
          } else {
            initialData = {
              obraId,
              date: dateStr,
              weather: {
                morning: { condition: '', workable: false, observation: '' },
                afternoon: { condition: '', workable: false, observation: '' }
              },
              workforce: [],
              equipment: [],
              activities: [],
              occurrences: [],
              materials: { received: [], used: [] },
              comments: [],
              photos: [],
              videos: [],
              attachments: [],
              status: 'draft'
            };
          }
        }
        
        // Fase 1: o estado da página é sempre mantido no formato legado.
        // A normalização para o schema unificado ocorre apenas nas bordas
        // (adapters por módulo) e, futuramente, na camada de persistência.
        setRdoData(initialData);
        initialDataRef.current = JSON.parse(JSON.stringify(initialData));
      } catch (err) {
        setError('Erro ao carregar dados do RDO');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadRDOData();
  }, [obraId, dateStr, copyLastRDOParam, toast]);
  
  const isSectionEnabled = (sectionKey: string) => {
    if (!reportDefaults) return true;
    return reportDefaults[sectionKey as keyof typeof reportDefaults];
  };
  
  const updateWeatherField = (period: 'morning' | 'afternoon', field: string, value: any) => {
    setRdoData(prev => ({
      ...prev,
      weather: {
        ...prev.weather,
        [period]: {
          ...prev.weather?.[period],
          [field]: value
        }
      }
    }));
  };

const updateWeatherIcon = (period: 'morning' | 'afternoon', icon: WeatherCondition) => {
  console.log('updateWeatherIcon called:', { period, icon });
  updateWeatherField(period, 'condition', icon);
};

  const handleAddWorkforce = (workforceData: any) => {
    if (editingWorkforceIndex !== null) {
      // Editando item existente
      const updatedWorkforce = [...(rdoData.workforce || [])];
      
      if (workforceData.type === "team") {
        // Para equipes consolidadas, preservar estrutura original
        updatedWorkforce[editingWorkforceIndex] = {
          ...workforceData,
          observations: typeof workforceData.observations === 'string' ? workforceData.observations : workforceData.observations || ""
        };
      } else {
        // Para indivíduos, manter lógica atual
        updatedWorkforce[editingWorkforceIndex] = {
          team: workforceData.team,
          function: workforceData.function,
          name: workforceData.name,
          type: workforceData.employeeType,
          assignedTeam: workforceData.assignedTeam,
          quantity: workforceData.quantity,
          observations: typeof workforceData.observations === 'string' ? workforceData.observations : workforceData.observations || ""
        };
      }
      
      setRdoData(prev => ({
        ...prev,
        workforce: updatedWorkforce
      }));
      
      setEditingWorkforceIndex(null);
    } else {
      // Adicionando novo item
      let newWorkforceItem;
      
      if (workforceData.type === "team") {
        // Para equipes consolidadas, preservar estrutura original
        newWorkforceItem = {
          ...workforceData,
          observations: typeof workforceData.observations === 'string' ? workforceData.observations : workforceData.observations || ""
        };
      } else {
        // Para indivíduos, manter lógica atual
        newWorkforceItem = {
          team: workforceData.team,
          function: workforceData.function,
          name: workforceData.name,
          type: workforceData.employeeType,
          assignedTeam: workforceData.assignedTeam,
          quantity: workforceData.quantity,
          observations: typeof workforceData.observations === 'string' ? workforceData.observations : workforceData.observations || ""
        };
      }

      setRdoData(prev => ({
        ...prev,
        workforce: [...(prev.workforce || []), newWorkforceItem]
      }));
    }
  };

  // Funções específicas para salvar edições dos modais
  const handleSaveIndividualEdit = (updatedWorkforce: any) => {
    if (editingWorkforceIndex === null) return;
    
    const updatedWorkforceList = [...(rdoData.workforce || [])];
    
    // Se quantidade for 0, remover o item
    if (updatedWorkforce.quantity === 0) {
      updatedWorkforceList.splice(editingWorkforceIndex, 1);
    } else {
      updatedWorkforceList[editingWorkforceIndex] = updatedWorkforce;
    }
    
    setRdoData(prev => ({
      ...prev,
      workforce: updatedWorkforceList
    }));
    
    setEditingWorkforceIndex(null);
    setEditingWorkforceData(null);
  };

  const handleSaveTeamEdit = (updatedTeam: any) => {
    if (editingWorkforceIndex === null) return;
    
    const updatedWorkforceList = [...(rdoData.workforce || [])];
    
    // Se updatedTeam for null, remover a equipe
    if (updatedTeam === null || updatedTeam.totalMembers === 0) {
      updatedWorkforceList.splice(editingWorkforceIndex, 1);
    } else {
      updatedWorkforceList[editingWorkforceIndex] = updatedTeam;
    }
    
    setRdoData(prev => ({
      ...prev,
      workforce: updatedWorkforceList
    }));
    
    setEditingWorkforceIndex(null);
    setEditingWorkforceData(null);
  };

  const handleAddEquipment = (equipmentData: any) => {
    setRdoData(prev => ({
      ...prev,
      equipment: [...(prev.equipment || []), equipmentData]
    }));
  };

  const handleAddMaterial = (materialData: any) => {
    const { type, ...data } = materialData;
    setRdoData(prev => ({
      ...prev,
      materials: {
        ...prev.materials,
        [type]: [...(prev.materials?.[type as keyof typeof prev.materials] || []), data]
      }
    }));
  };

  const handleDeleteMaterial = (type: 'received' | 'used', index: number) => {
    setRdoData(prev => ({
      ...prev,
      materials: {
        ...prev.materials,
        [type]: prev.materials?.[type]?.filter((_, i) => i !== index) || []
      }
    }));
  };

  const handleAddComment = () => {
    if (!newCommentText.trim()) return;
    
    const newComment = {
      id: crypto.randomUUID(),
      user: {
        id: "current-user",
        name: "Usuário Atual",
        role: "Engenheiro"
      },
      datetime: new Date().toISOString(),
      text: newCommentText,
      canEdit: true
    };

    setRdoData(prev => ({
      ...prev,
      comments: [...(prev.comments || []), newComment]
    }));

    setNewCommentText("");
    setShowCommentModal(false);
  };

  const handleAddCommentInternal = (text: string, parentId?: string) => {
    const newComment = {
      id: crypto.randomUUID(),
      user: {
        id: "current-user",
        name: "Usuário Atual",
        role: "Engenheiro"
      },
      datetime: new Date().toISOString(),
      text: text,
      canEdit: true,
      parentId: parentId
    };

    setRdoData(prev => ({
      ...prev,
      comments: [...(prev.comments || []), newComment]
    }));
  };

  const handleEditComment = (commentId: string, newText: string) => {
    setRdoData(prev => ({
      ...prev,
      comments: (prev.comments || []).map((comment: any) =>
        comment.id === commentId ? { ...comment, text: newText } : comment
      )
    }));
  };

  const handleDeleteComment = (commentId: string) => {
    setRdoData(prev => ({
      ...prev,
      comments: (prev.comments || []).filter((comment: any) => {
        // Remove comment and its replies
        return comment.id !== commentId && comment.parentId !== commentId;
      })
    }));
  };

  const handleDeletePhoto = (photoIndex: number) => {
    setRdoData(prev => ({
      ...prev,
      photos: prev.photos?.filter((_, index) => index !== photoIndex) || []
    }));
  };

  const handleDeleteVideo = (videoIndex: number) => {
    setRdoData(prev => ({
      ...prev,
      videos: prev.videos?.filter((_, index) => index !== videoIndex) || []
    }));
  };

  const openPhotoViewer = (photoIndex: number) => {
    setCurrentPhotoIndex(photoIndex);
    setPhotoViewerOpen(true);
  };

  const openVideoViewer = (videoIndex: number) => {
    setCurrentVideoIndex(videoIndex);
    setVideoViewerOpen(true);
  };

  const navigatePhoto = React.useCallback((direction: 'prev' | 'next') => {
    try {
      if (!rdoData.photos || rdoData.photos.length === 0) {
        console.warn('No photos available for navigation');
        return;
      }
      
      if (direction === 'next') {
        setCurrentPhotoIndex((prev) => {
          const nextIndex = (prev + 1) % rdoData.photos!.length;
          return nextIndex;
        });
      } else {
        setCurrentPhotoIndex((prev) => {
          const prevIndex = (prev - 1 + rdoData.photos!.length) % rdoData.photos!.length;
          return prevIndex;
        });
      }
    } catch (error) {
      console.error('Error navigating photo:', error);
      setPhotoViewerOpen(false);
    }
  }, [rdoData.photos]);

  const navigateVideo = React.useCallback((direction: 'prev' | 'next') => {
    try {
      if (!rdoData.videos || rdoData.videos.length === 0) {
        console.warn('No videos available for navigation');
        return;
      }
      
      if (direction === 'next') {
        setCurrentVideoIndex((prev) => {
          const nextIndex = (prev + 1) % rdoData.videos!.length;
          return nextIndex;
        });
      } else {
        setCurrentVideoIndex((prev) => {
          const prevIndex = (prev - 1 + rdoData.videos!.length) % rdoData.videos!.length;
          return prevIndex;
        });
      }
    } catch (error) {
      console.error('Error navigating video:', error);
      setVideoViewerOpen(false);
    }
  }, [rdoData.videos]);

  // Reset video when changing
  useEffect(() => {
    if (videoViewerOpen && videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.muted = true;
      videoRef.current.play().catch(() => {
        // Auto-play might be blocked by browser policy
      });
    }
  }, [currentVideoIndex, videoViewerOpen]);

  // Handle keyboard navigation with proper cleanup and stable dependencies
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      try {
        if (photoViewerOpen) {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            navigatePhoto('prev');
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            navigatePhoto('next');
          } else if (event.key === 'Escape') {
            event.preventDefault();
            setPhotoViewerOpen(false);
          }
        } else if (videoViewerOpen) {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            navigateVideo('prev');
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            navigateVideo('next');
          } else if (event.key === 'Escape') {
            event.preventDefault();
            setVideoViewerOpen(false);
          }
        }
      } catch (error) {
        console.error('Error handling keyboard event:', error);
      }
    };

    // Only add listener when modals are open
    if (photoViewerOpen || videoViewerOpen) {
      window.addEventListener('keydown', handleKeyDown);
      
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [photoViewerOpen, videoViewerOpen, navigatePhoto, navigateVideo]);

  const openCaptionEdit = (index: number, type: 'photo' | 'video', currentCaption: string) => {
    setEditingCaptionIndex(index);
    setEditingCaptionType(type);
    setEditingCaptionText(currentCaption || '');
    setShowCaptionEditModal(true);
  };

  const saveCaptionEdit = () => {
    if (editingCaptionIndex === null || !editingCaptionType) return;

    if (editingCaptionType === 'photo') {
      setRdoData(prev => ({
        ...prev,
        photos: prev.photos?.map((photo, index) => 
          index === editingCaptionIndex 
            ? { ...photo, caption: editingCaptionText }
            : photo
        ) || []
      }));
    } else {
      setRdoData(prev => ({
        ...prev,
        videos: prev.videos?.map((video, index) => 
          index === editingCaptionIndex 
            ? { ...video, title: editingCaptionText }
            : video
        ) || []
      }));
    }

    setShowCaptionEditModal(false);
    setEditingCaptionIndex(null);
    setEditingCaptionType(null);
    setEditingCaptionText('');
  };

  const generateCaptionWithAI = async () => {
    // Simular chamada de IA para gerar legenda
    const suggestions = [
      "Registro fotográfico das atividades de construção",
      "Documentação do progresso da obra",
      "Evidência das condições do canteiro",
      "Acompanhamento das etapas executivas",
      "Controle de qualidade dos serviços"
    ];
    
    const randomSuggestion = suggestions[Math.floor(Math.random() * suggestions.length)];
    setEditingCaptionText(randomSuggestion);
  };

  const handleSavePhotos = (photos: any[]) => {
    setRdoData(prev => ({
      ...prev,
      photos: [...(prev.photos || []), ...photos]
    }));
  };

  const handleSaveActivityPhotos = (photos: any[]) => {
    if (selectedActivityIndex === null) return;

    // Adicionar a referência da atividade às fotos
    const photosWithActivity = photos.map(photo => ({
      ...photo,
      activityIndex: selectedActivityIndex,
      activityId: `activity_${selectedActivityIndex}`,
      source: 'activity'
    }));

    setRdoData(prev => ({
      ...prev,
      photos: [...(prev.photos || []), ...photosWithActivity]
    }));

    // Reset states
    setSelectedActivityIndex(null);
    setShowActivityPhotoModal(false);

    toast({
      title: "Fotos adicionadas",
      description: `${photos.length} foto${photos.length !== 1 ? 's' : ''} adicionada${photos.length !== 1 ? 's' : ''} à atividade.`
    });
  };

  const handleSaveOccurrencePhotos = (photos: any[]) => {
    if (selectedOccurrenceIndex === null) return;

    // Adicionar fotos à ocorrência específica
    setRdoData(prev => {
      const updatedOccurrences = prev.occurrences?.map((occ, index) => 
        index === selectedOccurrenceIndex 
          ? { ...occ, photos: [...(occ.photos || []), ...photos] }
          : occ
      ) || [];

      // Verificar se as fotos devem ser ocultadas do container principal
      const currentOccurrence = updatedOccurrences[selectedOccurrenceIndex];
      const shouldHidePhotos = currentOccurrence?.hidePhotos || false;

      // Adicionar ao container principal apenas se não estiver marcado para ocultar
      const photosForMainContainer = shouldHidePhotos ? [] : photos.map(photo => ({
        ...photo,
        occurrenceIndex: selectedOccurrenceIndex,
        source: 'occurrence'
      }));

      return {
        ...prev,
        occurrences: updatedOccurrences,
        photos: [...(prev.photos || []), ...photosForMainContainer]
      };
    });

    // Reset states
    setSelectedOccurrenceIndex(null);
    setShowOccurrencePhotoModal(false);

    toast({
      title: "Fotos adicionadas",
      description: `${photos.length} foto${photos.length !== 1 ? 's' : ''} adicionada${photos.length !== 1 ? 's' : ''} à ocorrência.`
    });
  };

  // Função para contar fotos de uma atividade específica
  const getActivityPhotoCount = (activityIndex: number) => {
    return rdoData.photos?.filter(photo => (photo as any).activityIndex === activityIndex).length || 0;
  };

  // Função para filtrar fotos de uma atividade específica
  const getActivityPhotos = (activityIndex: number) => {
    return rdoData.photos?.filter(photo => (photo as any).activityIndex === activityIndex) || [];
  };

  // Função para contar fotos de uma ocorrência específica
  const getOccurrencePhotoCount = (occurrenceIndex: number) => {
    return rdoData.occurrences?.[occurrenceIndex]?.photos?.length || 0;
  };

  // Função para filtrar fotos de uma ocorrência específica
  const getOccurrencePhotos = (occurrenceIndex: number) => {
    return rdoData.occurrences?.[occurrenceIndex]?.photos || [];
  };

  // Função para abrir a galeria de fotos da atividade
  const handleOpenActivityGallery = (activityIndex: number) => {
    setSelectedActivityIndex(activityIndex);
    setShowActivityGalleryModal(true);
  };

  // Função para abrir a galeria de fotos da ocorrência
  const handleOpenOccurrenceGallery = (occurrenceIndex: number) => {
    setSelectedOccurrenceIndex(occurrenceIndex);
    setShowOccurrenceGalleryModal(true);
  };

  const handleSaveVideos = (videos: any[]) => {
    setRdoData(prev => ({
      ...prev,
      videos: [...(prev.videos || []), ...videos]
    }));
  };

  const handleSaveAttachments = (attachments: any[]) => {
    const attachmentData = attachments.map(att => ({
      id: att.id,
      name: att.file.name,
      type: att.file.name.split('.').pop()?.toLowerCase() || 'file',
      size: (att.file.size / (1024 * 1024)).toFixed(2) + ' MB',
      url: URL.createObjectURL(att.file),
      timestamp: new Date().toISOString(),
      uploadedBy: 'Usuário Atual',
      permissions: att.permissions,
      previewable: isPreviewable(att.file.name.split('.').pop()?.toLowerCase() || ''),
      versions: 1
    }));
    
    setRdoData(prev => ({
      ...prev,
      attachments: [...(prev.attachments || []), ...attachmentData]
    }));
  };

  const isPreviewable = (type: string): boolean => {
    const previewableTypes = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp'];
    return previewableTypes.includes(type.toLowerCase());
  };

  const handleDeleteAttachment = (index: number) => {
    setRdoData(prev => ({
      ...prev,
      attachments: prev.attachments?.filter((_, i) => i !== index) || []
    }));
  };
  
  const addWorkforceRow = () => {
    setRdoData(prev => ({
      ...prev,
      workforce: [...(prev.workforce || []), { 
        team: '', 
        function: '', 
        quantity: 1,
        observations: ''
      }]
    }));
  };
  
  const updateWorkforceRow = (index: number, field: string, value: any) => {
    setRdoData(prev => ({
      ...prev,
      workforce: prev.workforce?.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      ) || []
    }));
  };
  
  const removeWorkforceRow = (index: number) => {
    setRdoData(prev => ({
      ...prev,
      workforce: prev.workforce?.filter((_, i) => i !== index) || []
    }));
  };
  
  const addEquipmentRow = () => {
    setRdoData(prev => ({
      ...prev,
      equipment: [...(prev.equipment || []), { 
        type: '', 
        identification: '', 
        operator: '',
        idleTime: 0,
        condition: 'Bom'
      }]
    }));
  };
  
  const updateEquipmentRow = (index: number, field: string, value: any) => {
    setRdoData(prev => ({
      ...prev,
      equipment: prev.equipment?.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      ) || []
    }));
  };
  
  const removeEquipmentRow = (index: number) => {
    setRdoData(prev => ({
      ...prev,
      equipment: prev.equipment?.filter((_, i) => i !== index) || []
    }));
  };
  
  const addActivityRow = () => {
    setRdoData(prev => ({
      ...prev,
      activities: [...(prev.activities || []), { 
        activity: '', 
        location: '', 
        start: '',
        end: '',
        completion: 0, 
        responsible: '', 
        description: '',
        linkedTask: ''
      }]
    }));
  };
  
  const updateActivityRow = (index: number, field: string, value: any) => {
    setRdoData(prev => ({
      ...prev,
      activities: prev.activities?.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      ) || []
    }));
  };
  
  const removeActivityRow = (index: number) => {
    setRdoData(prev => ({
      ...prev,
      activities: prev.activities?.filter((_, i) => i !== index) || []
    }));
  };

  const addOccurrenceRow = () => {
    setRdoData(prev => ({
      ...prev,
      occurrences: [...(prev.occurrences || []), { 
        type: '', 
        description: '', 
        impact: 'Baixo',
        actionTaken: '',
        responsible: '',
        photos: [],
        hidePhotos: false
      }]
    }));
  };

  const updateOccurrenceRow = (index: number, field: string, value: any) => {
    setRdoData(prev => {
      const updatedOccurrences = prev.occurrences?.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      ) || [];

      // Se está mudando a configuração de ocultar fotos
      if (field === 'hidePhotos') {
        const occurrence = updatedOccurrences[index];
        const occurrencePhotos = occurrence?.photos || [];

        if (value) {
          // Se está ocultando (value = true), remover fotos desta ocorrência do container principal
          return {
            ...prev,
            occurrences: updatedOccurrences,
            photos: prev.photos?.filter(photo => 
              !((photo as any).occurrenceIndex === index && (photo as any).source === 'occurrence')
            ) || []
          };
        } else {
          // Se está mostrando (value = false), adicionar fotos desta ocorrência ao container principal
          const photosToAdd = occurrencePhotos.map(photo => ({
            ...photo,
            occurrenceIndex: index,
            source: 'occurrence'
          }));

          return {
            ...prev,
            occurrences: updatedOccurrences,
            photos: [...(prev.photos || []), ...photosToAdd]
          };
        }
      }

      // Para outros campos, apenas atualizar normalmente
      return {
        ...prev,
        occurrences: updatedOccurrences
      };
    });
  };

  const removeOccurrenceRow = (index: number) => {
    setRdoData(prev => ({
      ...prev,
      occurrences: prev.occurrences?.filter((_, i) => i !== index) || []
    }));
  };
  
  // Fase 3: persiste o RDO evitando duplicatas. Reaproveita o id já salvo nesta
  // sessão ou um rascunho existente para a mesma obra+data; caso contrário cria.
  const persistRDO = async (status: 'draft' | 'in_review') => {
    const payload = {
      obraId: rdoData.obraId,
      date: rdoData.date,
      weather: rdoData.weather,
      workforce: rdoData.workforce,
      equipment: rdoData.equipment,
      activities: rdoData.activities,
      occurrences: rdoData.occurrences,
      materials: rdoData.materials,
      comments: rdoData.comments,
      photos: rdoData.photos,
      videos: rdoData.videos,
      attachments: rdoData.attachments,
      status,
    };

    let existingId = savedRdoId;
    if (!existingId) {
      const existing = await getRDOByObraDate(rdoData.obraId, rdoData.date);
      existingId = existing?.id ?? null;
    }
    const result = existingId ? await updateRDO(existingId, payload) : await createRDO(payload);
    if (result) setSavedRdoId(result.id);
    return result;
  };

  const handleSaveDraft = async () => {
    if (!rdoData.obraId || !rdoData.date) return;

    try {
      const result = await persistRDO('draft');
      if (!result) throw new Error('Falha ao salvar rascunho');

      // Marca o estado atual como salvo (zera o controle de alterações pendentes).
      initialDataRef.current = JSON.parse(JSON.stringify(rdoData));
      setHasSaved(true);
      toast({
        title: savedRdoId ? "Rascunho atualizado" : "Rascunho salvo",
        description: "RDO salvo como rascunho com sucesso."
      });
    } catch (err) {
      console.error('Error saving RDO:', err);
      toast({
        title: "Erro",
        description: "Erro ao salvar RDO como rascunho.",
        variant: "destructive"
      });
    }
  };

  const handleFinalize = async () => {
    if (!rdoData.obraId || !rdoData.date) return;

    // Não faz sentido enviar para revisão um RDO totalmente vazio.
    const temConteudo =
      (rdoData.workforce?.length ?? 0) > 0 ||
      (rdoData.equipment?.length ?? 0) > 0 ||
      (rdoData.activities?.length ?? 0) > 0 ||
      (rdoData.occurrences?.length ?? 0) > 0 ||
      (rdoData.materials?.received?.length ?? 0) > 0 ||
      (rdoData.materials?.used?.length ?? 0) > 0;
    if (!temConteudo) {
      toast({
        title: "RDO vazio",
        description: "Adicione ao menos mão de obra, equipamento, atividade, material ou ocorrência antes de finalizar.",
        variant: "destructive"
      });
      return;
    }

    try {
      const result = await persistRDO('in_review');
      if (!result) throw new Error('Falha ao finalizar');

      initialDataRef.current = JSON.parse(JSON.stringify(rdoData));
      setHasSaved(true);

      // Integração: reflete o progresso das atividades vinculadas nas tarefas
      const tarefasAtualizadas = await syncActivitiesToTasks(rdoData.activities);
      toast({
        title: "RDO finalizado",
        description: tarefasAtualizadas > 0
          ? `RDO enviado para revisão. ${tarefasAtualizadas} tarefa(s) atualizada(s).`
          : "RDO finalizado e enviado para revisão."
      });

      // Navigate to RDO view page
      navigate(`/rdo?obra=${rdoData.obraId}&date=${rdoData.date}`);
    } catch (err) {
      console.error('Error finalizing RDO:', err);
      toast({
        title: "Erro",
        description: "Erro ao finalizar RDO.",
        variant: "destructive"
      });
    }
  };
  
  const isDirty = () => {
    if (!initialDataRef.current) return false;
    return JSON.stringify(rdoData) !== JSON.stringify(initialDataRef.current);
  };

  const onCloseAttempt = () => {
    // Sem alterações pendentes (nunca editou OU já salvou) — sai direto.
    if (!isDirty()) {
      safeExit();
      return;
    }

    // Há alterações não salvas — confirmar antes de sair.
    setConfirmExitOpen(true);
  };

  const confirmExitWithoutSaving = () => {
    setConfirmExitOpen(false);
    // Clear local data and navigate back
    if (initialDataRef.current) {
      setRdoData(initialDataRef.current);
    }
    safeExit();
  };

  const handleCancel = () => {
    onCloseAttempt();
  };
  
  if (isLoading || isLoadingDefaults) {
    return (
      <ErrorBoundary>
        <div className="min-h-screen bg-background px-4 py-4 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-pulse">Carregando...</div>
          </div>
        </div>
      </ErrorBoundary>
    );
  }
  
  if (error) {
    return (
      <ErrorBoundary>
        <div className="min-h-screen bg-background px-4 py-4">
          <Alert className="max-w-md mx-auto mt-8">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      </ErrorBoundary>
    );
  }
  
  return (
    <div className="min-h-screen bg-background px-4 py-4">
      <div className="max-w-5xl mx-auto space-y-3 pb-[5cm]">
        
        {/* Close Button */}
        <div className="flex justify-end print:hidden">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onCloseAttempt}
            className="h-8 w-8 p-0"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Header */}
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <h1 className="text-lg font-bold">Relatório Diário de Obra</h1>
                <p className="text-sm text-muted-foreground">{obra?.nomeObra || `Obra ${obraId}`}</p>
              </div>
              <div className="text-right space-y-1">
                <p className="text-sm font-medium">
                  {dateStr && formatYmd(dateStr)}
                </p>
                <Badge variant="outline">Rascunho</Badge>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="py-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Cliente</label>
                <p className="text-sm">{obra?.clienteObra || 'Incorporadora ABC Ltda.'}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Responsável</label>
                <p className="text-sm">{obra?.responsaveisObra?.[0] || 'João Silva'}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Endereço</label>
                <p className="text-sm">
                  {typeof obra?.endereco === 'object' && obra.endereco ? 
                    `${obra.endereco.rua}, ${obra.endereco.numero} - ${obra.endereco.cidade}/${obra.endereco.estado}` :
                    'Rua das Palmeiras, 123 - São Paulo/SP'
                  }
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Nº Relatório</label>
                <p className="text-sm">{reportNumber}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Condições Climáticas */}
        {isSectionEnabled('condicoesClimaticas') && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Cloud className="w-5 h-5" />
                Condições Climáticas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <WeatherIconHeader 
                    condition={rdoData.weather?.morning?.condition} 
                    label="Manhã" 
                  />
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Label>Condição</Label>
                      <WeatherIconButton
                        value={rdoData.weather?.morning?.condition}
                        onChange={(condition) => updateWeatherIcon('morning', condition)}
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={rdoData.weather?.morning?.workable || false}
                        onCheckedChange={(checked) => 
                          updateWeatherField('morning', 'workable', checked)
                        }
                      />
                      <Label>{rdoData.weather?.morning?.workable ? 'Praticável' : 'Impraticável'}</Label>
                    </div>
                    <div>
                      <Label htmlFor="morning-observation">Observação</Label>
                      <Textarea
                        id="morning-observation"
                        value={rdoData.weather?.morning?.observation || ''}
                        onChange={(e) => updateWeatherField('morning', 'observation', e.target.value)}
                        placeholder="Observações sobre as condições climáticas"
                        rows={2}
                      />
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <WeatherIconHeader 
                    condition={rdoData.weather?.afternoon?.condition} 
                    label="Tarde" 
                  />
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Label>Condição</Label>
                      <WeatherIconButton
                        value={rdoData.weather?.afternoon?.condition}
                        onChange={(condition) => updateWeatherIcon('afternoon', condition)}
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={rdoData.weather?.afternoon?.workable || false}
                        onCheckedChange={(checked) => 
                          updateWeatherField('afternoon', 'workable', checked)
                        }
                      />
                      <Label>{rdoData.weather?.afternoon?.workable ? 'Praticável' : 'Impraticável'}</Label>
                    </div>
                    <div>
                      <Label htmlFor="afternoon-observation">Observação</Label>
                      <Textarea
                        id="afternoon-observation"
                        value={rdoData.weather?.afternoon?.observation || ''}
                        onChange={(e) => updateWeatherField('afternoon', 'observation', e.target.value)}
                        placeholder="Observações sobre as condições climáticas"
                        rows={2}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Mão de Obra */}
        {isSectionEnabled('maoDeObra') && (
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Mão de Obra ({rdoData.workforce ? rdoData.workforce.reduce((sum, item) => sum + (item.quantity || 0), 0) : 0})
                </CardTitle>
                 <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newMode = workforceViewMode === 'cards' ? 'table' : 'cards';
                          setWorkforceViewMode(newMode);
                          updatePreference('workforce', newMode);
                        }}
                      >
                       {workforceViewMode === 'cards' ? <List className="w-4 h-4" /> : <LayoutGrid className="w-4 h-4" />}
                     </Button>
                    <Button onClick={() => setShowWorkforceModal(true)} size="sm" variant="outline" className="min-h-11 sm:min-h-9">
                      <Plus className="w-4 h-4 mr-2" />
                      Adicionar
                    </Button>
                 </div>
              </div>
            </CardHeader>
            <CardContent>
              {rdoData.workforce && rdoData.workforce.length > 0 ? (
                <WorkforceCardsLayout 
                  workforce={rdoData.workforce} 
                  showTotals={true} 
                  viewMode={workforceViewMode}
                  onEdit={handleEditWorkforce}
                  onDelete={handleDeleteWorkforce}
                />
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhuma mão de obra adicionada</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* Equipamentos */}
        {isSectionEnabled('equipamentos') && (
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  Equipamentos ({rdoData.equipment ? rdoData.equipment.length : 0})
                </CardTitle>
                <Button onClick={() => setShowEquipmentModal(true)} size="sm" variant="outline" className="min-h-11 sm:min-h-9">
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {rdoData.equipment && rdoData.equipment.length > 0 ? (
                <EquipmentCardsLayout 
                  equipment={rdoData.equipment.map(eq => ({
                    tipo: eq.type || '',
                    identificacao: eq.identification || '',
                    condicao: eq.condition || '',
                    operador: eq.operator || '',
                    tempoOcioso: eq.idleTime?.toString() || '',
                    status: (eq as any).ownershipType === 'locado' ? 'locado' : 'proprio',
                    quantity: (eq as any).quantity || 1
                  }))} 
                  showTotals={true}
                  onDelete={handleDeleteEquipment}
                  onEdit={handleEditEquipment}
                />
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  <Timer className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhum equipamento adicionado</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* Atividades */}
        {isSectionEnabled('atividades') && (
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Atividades
                </CardTitle>
                <Button onClick={addActivityRow} size="sm" variant="outline" className="min-h-11 sm:min-h-9">
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs w-1/4">Atividade</TableHead>
                      <TableHead className="text-xs w-1/5">Local</TableHead>
                      <TableHead className="text-xs text-center w-20">% Concluída</TableHead>
                      <TableHead className="text-xs w-1/6">Responsável</TableHead>
                      <TableHead className="text-xs w-1/3">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rdoData.activities?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Input
                            value={item.activity}
                            onChange={(e) => updateActivityRow(index, 'activity', e.target.value)}
                            placeholder="Ex: Concretagem da laje do 3º andar"
                            className="text-xs"
                          />
                          {/* Vínculo com o Gestor de Tarefas: reflete o % no status/progresso da tarefa ao finalizar */}
                          <Select
                            value={item.linkedTask || 'none'}
                            onValueChange={(v) => {
                              const task = obraTasks.find((t) => t.id === v);
                              updateActivityRow(index, 'linkedTask', v === 'none' ? '' : v);
                              updateActivityRow(index, 'linkedTaskTitle', task?.title || '');
                            }}
                          >
                            <SelectTrigger className="text-xs h-7 mt-1">
                              <SelectValue placeholder="Vincular tarefa (opcional)" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Sem tarefa vinculada</SelectItem>
                              {obraTasks.length === 0 && (
                                <div className="px-2 py-1.5 text-xs text-muted-foreground">
                                  Nenhuma tarefa nesta obra
                                </div>
                              )}
                              {obraTasks.map((t) => (
                                <SelectItem key={t.id} value={t.id} className="text-xs">
                                  {t.title}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input
                            value={item.location || ''}
                            onChange={(e) => updateActivityRow(index, 'location', e.target.value)}
                            placeholder="Ex: Bloco A - 3º andar"
                            className="text-xs"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={item.completion || 0}
                            onChange={(e) => updateActivityRow(index, 'completion', parseInt(e.target.value) || 0)}
                            min="0"
                            max="100"
                            className="text-xs text-center"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={item.responsible || ''}
                            onChange={(e) => updateActivityRow(index, 'responsible', e.target.value)}
                            placeholder="Ex: João Silva"
                            className="text-xs"
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 flex-wrap">
                            {/* Upload button */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const fileInput = document.createElement('input');
                                fileInput.type = 'file';
                                fileInput.accept = 'image/jpeg,image/jpg,image/png,image/heic';
                                fileInput.multiple = true;
                                fileInput.onchange = (e) => {
                                  const files = (e.target as HTMLInputElement).files;
                                  if (files && files.length > 0) {
                                    setSelectedActivityIndex(index);
                                    setSelectedActivityFiles(files);
                                    setShowActivityPhotoModal(true);
                                  }
                                };
                                fileInput.click();
                              }}
                              aria-label="Enviar fotos da atividade"
                              className="h-8 w-8 p-0"
                            >
                              <Upload className="w-4 h-4" />
                            </Button>


                            {/* Delete activity button */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeActivityRow(index)}
                              className="h-8 w-8 p-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>

                            {/* Photo thumbnails */}
                            {(() => {
                              const activityPhotos = getActivityPhotos(index);
                              const displayPhotos = activityPhotos.slice(0, 2);
                              const remainingCount = Math.max(0, activityPhotos.length - 2);
                              
                              return (
                                <>
                                  {displayPhotos.map((photo: any, photoIndex: number) => (
                                    <div
                                      key={photoIndex}
                                      className="w-8 h-8 border rounded cursor-pointer hover:scale-105 transition-transform overflow-hidden bg-muted"
                                      onClick={() => handleOpenActivityGallery(index)}
                                      title={photo.caption || 'Ver foto'}
                                    >
                                      {photo.url ? (
                                        <img 
                                          src={photo.url} 
                                          alt={photo.caption} 
                                          className="w-full h-full object-cover" 
                                        />
                                      ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                          <Image className="w-3 h-3 text-muted-foreground" />
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                  
                                  {remainingCount > 0 && (
                                    <div
                                      className="w-8 h-8 border rounded cursor-pointer hover:scale-105 transition-transform bg-muted flex items-center justify-center text-xs font-medium"
                                      onClick={() => handleOpenActivityGallery(index)}
                                      title={`Ver mais ${remainingCount} foto${remainingCount !== 1 ? 's' : ''}`}
                                    >
                                      +{remainingCount}
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!rdoData.activities || rdoData.activities.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground">
                          Nenhuma atividade adicionada
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Ocorrências */}
        {isSectionEnabled('ocorrencias') && (
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Ocorrências ({rdoData.occurrences?.length || 0})
                </CardTitle>
                <Button onClick={addOccurrenceRow} size="sm" variant="outline" className="min-h-11 sm:min-h-9">
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {rdoData.occurrences?.map((occurrence, index) => (
                  <div key={index} className="border rounded-lg p-2 space-y-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h4 className="font-medium text-sm">Ocorrência {index + 1}</h4>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const fileInput = document.createElement('input');
                            fileInput.type = 'file';
                            fileInput.accept = 'image/jpeg,image/jpg,image/png,image/heic';
                            fileInput.multiple = true;
                            fileInput.onchange = (e) => {
                              const files = (e.target as HTMLInputElement).files;
                              if (files && files.length > 0) {
                                setSelectedOccurrenceIndex(index);
                                setSelectedOccurrenceFiles(files);
                                setShowOccurrencePhotoModal(true);
                              }
                            };
                            fileInput.click();
                          }}
                          aria-label="Enviar fotos da ocorrência"
                          className="h-8 w-8 p-0"
                        >
                          <Upload className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeOccurrenceRow(index)}
                          className="h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor={`occurrence-type-${index}`}>Tipo</Label>
                        <Input
                          id={`occurrence-type-${index}`}
                          value={occurrence.type}
                          onChange={(e) => updateOccurrenceRow(index, 'type', e.target.value)}
                          placeholder="Ex: Segurança, Qualidade, Prazo"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`occurrence-impact-${index}`}>Impacto</Label>
                        <Select
                          value={occurrence.impact}
                          onValueChange={(value) => updateOccurrenceRow(index, 'impact', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Baixo">Baixo</SelectItem>
                            <SelectItem value="Médio">Médio</SelectItem>
                            <SelectItem value="Alto">Alto</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="md:col-span-2 grid md:grid-cols-2 gap-4 items-start">
                        <div>
                          <Label htmlFor={`occurrence-responsible-${index}`}>Responsável</Label>
                          <Input
                            id={`occurrence-responsible-${index}`}
                            value={occurrence.responsible}
                            onChange={(e) => updateOccurrenceRow(index, 'responsible', e.target.value)}
                            placeholder="Ex: Técnico de Segurança"
                          />
                        </div>
                        
                        {/* Photo thumbnails */}
                        <div className="flex items-end gap-2 flex-wrap">
                          {(() => {
                            const occurrencePhotos = getOccurrencePhotos(index);
                            const displayPhotos = occurrencePhotos.slice(0, 2);
                            const remainingCount = Math.max(0, occurrencePhotos.length - 2);
                            
                            return (
                              <>
                                {displayPhotos.map((photo: any, photoIndex: number) => (
                                  <div
                                    key={photoIndex}
                                    className="w-16 h-16 border rounded cursor-pointer hover:scale-105 transition-transform overflow-hidden bg-muted"
                                    onClick={() => handleOpenOccurrenceGallery(index)}
                                    title={photo.caption || 'Ver foto'}
                                  >
                                    {photo.url ? (
                                      <img 
                                        src={photo.url} 
                                        alt={photo.caption} 
                                        className="w-full h-full object-cover" 
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center">
                                        <Image className="w-4 h-4 text-muted-foreground" />
                                      </div>
                                    )}
                                  </div>
                                ))}
                                
                                {remainingCount > 0 && (
                                  <div
                                    className="w-16 h-16 border rounded cursor-pointer hover:scale-105 transition-transform bg-muted flex items-center justify-center text-sm font-medium"
                                    onClick={() => handleOpenOccurrenceGallery(index)}
                                    title={`Ver mais ${remainingCount} foto${remainingCount !== 1 ? 's' : ''}`}
                                  >
                                    +{remainingCount}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor={`occurrence-description-${index}`}>Descrição</Label>
                      <Textarea
                        id={`occurrence-description-${index}`}
                        value={occurrence.description}
                        onChange={(e) => updateOccurrenceRow(index, 'description', e.target.value)}
                        placeholder="Descreva detalhadamente a ocorrência"
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label htmlFor={`occurrence-action-${index}`}>Ação Tomada</Label>
                      <Textarea
                        id={`occurrence-action-${index}`}
                        value={occurrence.actionTaken}
                        onChange={(e) => updateOccurrenceRow(index, 'actionTaken', e.target.value)}
                        placeholder="Descreva as ações tomadas para resolver a ocorrência"
                        rows={2}
                      />
                    </div>
                    
                    {/* Hide Photos Checkbox */}
                    {getOccurrencePhotoCount(index) > 0 && (
                      <div className="flex items-center space-x-2">
                        <Switch
                          checked={occurrence.hidePhotos || false}
                          onCheckedChange={(checked) => updateOccurrenceRow(index, 'hidePhotos', checked)}
                        />
                        <Label className="text-sm">Ocultar fotos do container principal</Label>
                      </div>
                    )}
                  </div>
                ))}
                {(!rdoData.occurrences || rdoData.occurrences.length === 0) && (
                  <div className="text-center text-muted-foreground py-8">
                    Nenhuma ocorrência registrada
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Materiais */}
        {isSectionEnabled('materiais') && (
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Paperclip className="w-5 h-5" />
                  Materiais
                </CardTitle>
                <Button onClick={() => setShowMaterialModal(true)} size="sm" variant="outline" className="min-h-11 sm:min-h-9">
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Material
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="received" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="received">Recebidos</TabsTrigger>
                  <TabsTrigger value="used">Utilizados</TabsTrigger>
                </TabsList>
                <TabsContent value="received" className="space-y-4">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Material</TableHead>
                          <TableHead className="text-xs text-center">Quantidade</TableHead>
                          <TableHead className="text-xs text-center">Unidade</TableHead>
                          <TableHead className="text-xs">Fornecedor</TableHead>
                          <TableHead className="text-xs">Documento</TableHead>
                          <TableHead className="text-xs">Observações</TableHead>
                          <TableHead className="text-xs text-center">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rdoData.materials?.received?.map((item: any, index: number) => (
                          <TableRow key={index}>
                            <TableCell className="text-xs">{item.material}</TableCell>
                            <TableCell className="text-xs text-center">{item.quantity}</TableCell>
                            <TableCell className="text-xs text-center">{item.unit}</TableCell>
                            <TableCell className="text-xs">{item.supplier}</TableCell>
                            <TableCell className="text-xs">{item.document}</TableCell>
                            <TableCell className="text-xs">{item.observations}</TableCell>
                            <TableCell className="text-xs text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteMaterial('received', index)}
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        )) || (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center text-muted-foreground">
                              Nenhum material recebido
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>
                <TabsContent value="used" className="space-y-4">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Material</TableHead>
                          <TableHead className="text-xs text-center">Quantidade</TableHead>
                          <TableHead className="text-xs text-center">Unidade</TableHead>
                          <TableHead className="text-xs">Local</TableHead>
                          <TableHead className="text-xs">Observações</TableHead>
                          <TableHead className="text-xs text-center">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rdoData.materials?.used?.map((item: any, index: number) => (
                          <TableRow key={index}>
                            <TableCell className="text-xs">{item.material}</TableCell>
                            <TableCell className="text-xs text-center">{item.quantity}</TableCell>
                            <TableCell className="text-xs text-center">{item.unit}</TableCell>
                            <TableCell className="text-xs">{item.location}</TableCell>
                            <TableCell className="text-xs">{item.observations}</TableCell>
                            <TableCell className="text-xs text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteMaterial('used', index)}
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        )) || (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center text-muted-foreground">
                              Nenhum material utilizado
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        )}

        {/* Comentários */}
        {isSectionEnabled('comentarios') && (
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Comentários
                </CardTitle>
                <Dialog open={showCommentModal} onOpenChange={setShowCommentModal}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline" className="min-h-11 sm:min-h-9">
                      <Plus className="w-4 h-4 mr-2" />
                      Adicionar Comentário
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Adicionar Comentário</DialogTitle>
                      <DialogDescription>
                        Adicione um comentário sobre o relatório diário.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Comentário</Label>
                        <Textarea 
                          placeholder="Digite seu comentário..." 
                          rows={4}
                          value={newCommentText}
                          onChange={(e) => setNewCommentText(e.target.value)}
                          onKeyDown={(e) => e.stopPropagation()}
                          onKeyUp={(e) => e.stopPropagation()}
                          onKeyPress={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div className="flex justify-end space-x-2">
                        <Button variant="outline" onClick={() => setShowCommentModal(false)}>
                          Cancelar
                        </Button>
                        <Button 
                          onClick={handleAddComment}
                          disabled={!newCommentText.trim()}
                        >
                          Adicionar
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <CommentsSection 
                comments={(rdoData.comments || []) as any} 
                reportStatus={rdoData.status || 'draft'} 
                currentUserId="current-user"
                onAddComment={handleAddCommentInternal}
                onEditComment={handleEditComment}
                onDeleteComment={handleDeleteComment}
              />
            </CardContent>
          </Card>
        )}

        {/* Fotos */}
        {isSectionEnabled('fotos') && (
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Camera className="w-5 h-5" />
                  Fotos ({rdoData.photos?.length || 0})
                </CardTitle>
                <Button onClick={() => setShowPhotoModal(true)} size="sm" variant="outline" className="min-h-11 sm:min-h-9">
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Fotos
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {rdoData.photos && rdoData.photos.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {rdoData.photos.map((photo: any, index: number) => (
                    <div key={index} className="border rounded-lg p-2 space-y-2 relative">
                      <div className="absolute top-1 right-1 flex gap-1 z-10">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 bg-background/80 hover:bg-background text-foreground"
                          onClick={() => openCaptionEdit(index, 'photo', photo.caption)}
                          title="Editar legenda"
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 bg-destructive/10 hover:bg-destructive/20 text-destructive"
                          onClick={() => handleDeletePhoto(index)}
                          title="Remover foto"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="aspect-square bg-muted rounded overflow-hidden cursor-pointer" onClick={() => openPhotoViewer(index)}>
                        {photo.url ? (
                          <img src={photo.url} alt={photo.caption} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Image className="w-8 h-8 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2">{photo.caption}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  Nenhuma foto adicionada
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Vídeos */}
        {isSectionEnabled('videos') && (
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Video className="w-5 h-5" />
                  Vídeos ({rdoData.videos?.length || 0})
                </CardTitle>
                <Button onClick={() => setShowVideoModal(true)} size="sm" variant="outline" className="min-h-11 sm:min-h-9">
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Vídeos
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {rdoData.videos && rdoData.videos.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {rdoData.videos.map((video: any, index: number) => (
                    <div key={index} className="border rounded-lg p-4 space-y-2 relative">
                      <div className="absolute top-2 right-2 flex gap-1 z-10">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 bg-background/80 hover:bg-background text-foreground"
                          onClick={() => openCaptionEdit(index, 'video', video.title)}
                          title="Editar título"
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 bg-destructive/10 hover:bg-destructive/20 text-destructive"
                          onClick={() => handleDeleteVideo(index)}
                          title="Remover vídeo"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="aspect-video bg-muted rounded overflow-hidden flex items-center justify-center cursor-pointer" onClick={() => openVideoViewer(index)}>
                        <FileVideo className="w-12 h-12 text-muted-foreground" />
                      </div>
                      <h4 className="font-medium text-sm">{video.title}</h4>
                      {video.duration && (
                        <p className="text-xs text-muted-foreground">Duração: {video.duration}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  Nenhum vídeo adicionado
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Anexos */}
        {isSectionEnabled('anexos') && (
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Paperclip className="w-5 h-5" />
                  Anexos ({rdoData.attachments?.length || 0})
                </CardTitle>
                <Button onClick={() => setShowAttachmentModal(true)} size="sm" variant="outline" className="min-h-11 sm:min-h-9">
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Anexos
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <AttachmentsSection 
                attachments={(rdoData.attachments || []) as any[]} 
                onDelete={handleDeleteAttachment}
              />
            </CardContent>
          </Card>
        )}
        
      </div>
      
      {/* Exit Confirmation Dialog */}
      <AlertDialog open={confirmExitOpen} onOpenChange={setConfirmExitOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sair sem salvar?</AlertDialogTitle>
            <AlertDialogDescription>
              Você fez alterações. Sair agora vai descartar os dados não salvos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmExitWithoutSaving}>
              Sair sem salvar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Fixed Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-background border-t p-4 shadow-lg print:hidden">
        <div className="max-w-5xl mx-auto flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center">
          <div className="text-sm text-muted-foreground">
            {isDirty() && (
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
                Alterações não salvas
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <Button variant="outline" onClick={handleCancel}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Cancelar
            </Button>
            <Button variant="outline" onClick={handleSaveDraft} disabled={!isDirty()}>
              <Save className="w-4 h-4 mr-2" />
              {savedRdoId ? "Atualizar Rascunho" : "Salvar Rascunho"}
            </Button>
            <Button onClick={handleFinalize}>
              <Check className="w-4 h-4 mr-2" />
              Finalizar
            </Button>
          </div>
        </div>
      </div>

      {/* Dialog Components */}
        <DialogAddWorkforce
          open={showWorkforceModal}
          onOpenChange={(open) => {
            setShowWorkforceModal(open);
            if (!open) {
              setEditingWorkforceIndex(null);
            }
          }}
          onAdd={handleAddWorkforce}
          editingData={editingWorkforceIndex !== null ? (
            editingWorkforceIndex === -1 ? 
              { isTeamEdit: true } : 
              rdoData.workforce?.[editingWorkforceIndex]
          ) : undefined}
          alreadyAddedTeamNames={rdoData.workforce?.reduce((teams: string[], item: any) => {
            // For consolidated teams (have teamName property)
            if (item.teamName && !teams.includes(item.teamName)) {
              teams.push(item.teamName);
            }
            // For individual items with team assignment
            if (item.team && item.team !== 'sem-equipe' && !teams.includes(item.team)) {
              teams.push(item.team);
            }
            return teams;
          }, []) || []}
        />

        {/* Novos modais de edição específicos */}
        <WorkforceIndividualEditModal
          open={showIndividualEditModal}
          onOpenChange={(open) => {
            setShowIndividualEditModal(open);
            if (!open) {
              setEditingWorkforceIndex(null);
              setEditingWorkforceData(null);
            }
          }}
          workforce={editingWorkforceData || {}}
          onSave={handleSaveIndividualEdit}
        />

        <WorkforceTeamEditModal
          open={showTeamEditModal}
          onOpenChange={(open) => {
            setShowTeamEditModal(open);
            if (!open) {
              setEditingWorkforceIndex(null);
              setEditingWorkforceData(null);
            }
          }}
          teamData={editingWorkforceData || {}}
          onSave={handleSaveTeamEdit}
        />

      <DialogAddEquipment
        open={showEquipmentModal}
        onOpenChange={setShowEquipmentModal}
        onAdd={handleAddEquipment}
      />

      <DialogAddMaterial
        open={showMaterialModal}
        onOpenChange={setShowMaterialModal}
        onAdd={handleAddMaterial}
      />

      <DialogFotos
        open={showPhotoModal}
        onOpenChange={setShowPhotoModal}
        onSave={handleSavePhotos}
      />

      <DialogFotos
        open={showActivityPhotoModal}
        onOpenChange={(open) => {
          setShowActivityPhotoModal(open);
          if (!open) {
            setSelectedActivityFiles(null);
          }
        }}
        onSave={handleSaveActivityPhotos}
        activityName={selectedActivityIndex !== null ? rdoData.activities?.[selectedActivityIndex]?.activity : undefined}
        activityIndex={selectedActivityIndex}
        initialFiles={selectedActivityFiles}
      />

      <DialogFotos
        open={showOccurrencePhotoModal}
        onOpenChange={(open) => {
          setShowOccurrencePhotoModal(open);
          if (!open) {
            setSelectedOccurrenceFiles(null);
          }
        }}
        onSave={handleSaveOccurrencePhotos}
        title={selectedOccurrenceIndex !== null ? 
          `Adicionar Fotos - Ocorrência ${selectedOccurrenceIndex + 1}` : 
          "Adicionar Fotos da Ocorrência"
        }
        initialFiles={selectedOccurrenceFiles}
      />

      <DialogVideos
        open={showVideoModal}
        onOpenChange={setShowVideoModal}
        onSave={handleSaveVideos}
      />

      <DialogAnexos
        open={showAttachmentModal}
        onOpenChange={setShowAttachmentModal}
        onSave={handleSaveAttachments}
      />
      
      {/* Activity Photos Gallery Modal */}
      <Dialog open={showActivityGalleryModal} onOpenChange={setShowActivityGalleryModal}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              Fotos da Atividade {selectedActivityIndex !== null ? selectedActivityIndex + 1 : ''}
            </DialogTitle>
            <DialogDescription>
              Visualize e gerencie todas as fotos relacionadas a esta atividade.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 max-h-[calc(90vh-8rem)] overflow-y-auto">
            {selectedActivityIndex !== null && getActivityPhotos(selectedActivityIndex).length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {getActivityPhotos(selectedActivityIndex).map((photo: any, index: number) => (
                  <div key={index} className="border rounded-lg p-2 space-y-2">
                    <div className="aspect-square bg-muted rounded overflow-hidden relative group">
                      {photo.url ? (
                        <img 
                          src={photo.url} 
                          alt={photo.caption} 
                          className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform" 
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image className="w-8 h-8 text-muted-foreground" />
                        </div>
                      )}
                      
                      {/* Overlay actions */}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            const newCaption = prompt('Editar legenda:', photo.caption || '');
                            if (newCaption !== null) {
                              setRdoData(prev => ({
                                ...prev,
                                photos: prev.photos?.map(p => 
                                  p.id === photo.id ? { ...p, caption: newCaption } : p
                                ) || []
                              }));
                              toast({
                                title: "Legenda atualizada",
                                description: "Legenda da foto foi atualizada com sucesso."
                              });
                            }
                          }}
                          className="h-8 w-8 p-0"
                        >
                          <FileText className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => {
                            setRdoData(prev => ({
                              ...prev,
                              photos: prev.photos?.filter(p => 
                                !((p as any).activityIndex === selectedActivityIndex && p.id === photo.id)
                              ) || []
                            }));
                            toast({
                              title: "Foto removida",
                              description: "Foto removida da atividade com sucesso."
                            });
                          }}
                          className="h-8 w-8 p-0"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground line-clamp-2" title={photo.caption}>
                        {photo.caption || 'Sem legenda'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                <Image className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhuma foto encontrada para esta atividade</p>
              </div>
            )}
          </div>

          <div className="flex justify-between items-center pt-4 border-t">
            <div className="text-sm text-muted-foreground">
              {selectedActivityIndex !== null ? 
                `${getActivityPhotoCount(selectedActivityIndex)} foto${getActivityPhotoCount(selectedActivityIndex) !== 1 ? 's' : ''}` : 
                '0 fotos'
              }
            </div>
            <Button onClick={() => setShowActivityGalleryModal(false)}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Occurrence Photos Gallery Modal */}
      <Dialog open={showOccurrenceGalleryModal} onOpenChange={setShowOccurrenceGalleryModal}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              Fotos da Ocorrência {selectedOccurrenceIndex !== null ? selectedOccurrenceIndex + 1 : ''}
            </DialogTitle>
            <DialogDescription>
              Visualize e gerencie todas as fotos relacionadas a esta ocorrência.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 max-h-[calc(90vh-8rem)] overflow-y-auto">
            {selectedOccurrenceIndex !== null && getOccurrencePhotos(selectedOccurrenceIndex).length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {getOccurrencePhotos(selectedOccurrenceIndex).map((photo: any, index: number) => (
                  <div key={index} className="border rounded-lg p-2 space-y-2">
                    <div className="aspect-square bg-muted rounded overflow-hidden relative group">
                      {photo.url ? (
                        <img 
                          src={photo.url} 
                          alt={photo.caption} 
                          className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform" 
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image className="w-8 h-8 text-muted-foreground" />
                        </div>
                      )}
                      
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => {
                            setRdoData(prev => ({
                              ...prev,
                              occurrences: prev.occurrences?.map((occ, occIndex) => 
                                occIndex === selectedOccurrenceIndex 
                                  ? { ...occ, photos: occ.photos?.filter((p, pIndex) => pIndex !== index) }
                                  : occ
                              ) || []
                            }));
                            toast({
                              title: "Foto removida",
                              description: "Foto removida da ocorrência com sucesso."
                            });
                          }}
                          className="h-8 w-8 p-0"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground line-clamp-2" title={photo.caption}>
                        {photo.caption || 'Sem legenda'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                <Image className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhuma foto encontrada para esta ocorrência</p>
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <Button onClick={() => setShowOccurrenceGalleryModal(false)}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Equipment Edit Modal */}
      {editingEquipmentIndex !== null && rdoData.equipment && (
        <EditEquipmentModal
          open={showEditEquipmentModal}
          onOpenChange={setShowEditEquipmentModal}
          equipment={{
            tipo: rdoData.equipment[editingEquipmentIndex].type || '',
            identificacao: rdoData.equipment[editingEquipmentIndex].identification || '',
            condicao: rdoData.equipment[editingEquipmentIndex].condition || '',
            operador: rdoData.equipment[editingEquipmentIndex].operator || '',
            tempoOcioso: rdoData.equipment[editingEquipmentIndex].idleTime?.toString() || '',
            status: (rdoData.equipment[editingEquipmentIndex] as any).ownershipType === 'locado' ? 'locado' : 'proprio',
            quantity: (rdoData.equipment[editingEquipmentIndex] as any).quantity || 1,
            leaseExpiryDate: (rdoData.equipment[editingEquipmentIndex] as any).leaseExpiryDate
          }}
          onSave={handleSaveEquipmentEdit}
          onDelete={handleDeleteEquipmentFromEdit}
        />
      )}
      
      {/* Photo Viewer Modal */}
      <Dialog open={photoViewerOpen} onOpenChange={setPhotoViewerOpen}>
        <DialogContent className="max-w-4xl w-[90vw] h-[90vh] p-0">
          <DialogHeader className="sr-only">
            <DialogTitle>Visualizador de Fotos</DialogTitle>
            <DialogDescription>
              Visualize as fotos do relatório com navegação por teclado.
            </DialogDescription>
          </DialogHeader>
          <div className="relative h-full flex flex-col">
            {rdoData.photos && rdoData.photos.length > 0 && (
              <>
                {/* Photo Display Area */}
                <div className="flex-1 relative bg-black rounded-t-lg overflow-hidden">
                  <img
                    src={rdoData.photos[currentPhotoIndex]?.url}
                    alt={rdoData.photos[currentPhotoIndex]?.caption}
                    className="w-full h-full object-contain"
                  />
                  
                  {/* Navigation Buttons */}
                  {rdoData.photos.length > 1 && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white"
                        onClick={() => navigatePhoto('prev')}
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white"
                        onClick={() => navigatePhoto('next')}
                      >
                        <ChevronRight className="w-6 h-6" />
                      </Button>
                    </>
                  )}
                  
                  {/* Close Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white"
                    onClick={() => setPhotoViewerOpen(false)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                  
                  {/* Photo Counter */}
                  {rdoData.photos.length > 1 && (
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                      {currentPhotoIndex + 1} / {rdoData.photos.length}
                    </div>
                  )}
                </div>
                
                {/* Caption Area */}
                {rdoData.photos[currentPhotoIndex]?.caption && (
                  <div className="p-4 bg-background border-t">
                    <p className="text-sm text-center text-muted-foreground">
                      {rdoData.photos[currentPhotoIndex].caption}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Video Viewer Modal */}
      <Dialog open={videoViewerOpen} onOpenChange={setVideoViewerOpen}>
        <DialogContent className="max-w-5xl w-[95vw] h-[90vh] p-0">
          <DialogHeader className="sr-only">
            <DialogTitle>Visualizador de Vídeos</DialogTitle>
            <DialogDescription>
              Visualize os vídeos do relatório com navegação por teclado.
            </DialogDescription>
          </DialogHeader>
          <div className="relative h-full flex flex-col bg-black rounded-lg overflow-hidden">
            {rdoData.videos && rdoData.videos.length > 0 && (
              <>
                {/* Video Display Area */}
                <div className="flex-1 relative flex items-center justify-center p-4">
                  <div className="w-full max-w-4xl aspect-video bg-black rounded-lg overflow-hidden relative">
                  <video
                    ref={videoRef}
                    key={currentVideoIndex} // Force re-render when video changes
                    className="w-full h-full object-contain"
                    controls
                    muted
                    autoPlay
                    preload="metadata"
                    onError={(e) => {
                      console.error('Video loading error:', e);
                      // Handle video error gracefully
                    }}
                    onLoadStart={() => {
                      console.log('Video loading started');
                    }}
                  >
                    {rdoData.videos?.[currentVideoIndex]?.url && (
                      <source src={rdoData.videos[currentVideoIndex].url} type="video/mp4" />
                    )}
                    Seu navegador não suporta reprodução de vídeo.
                  </video>
                    
                    {/* Navigation Arrows */}
                    {rdoData.videos.length > 1 && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white border-0 transition-all duration-200 hover:scale-110"
                          onClick={() => navigateVideo('prev')}
                        >
                          <ChevronLeft className="w-8 h-8" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white border-0 transition-all duration-200 hover:scale-110"
                          onClick={() => navigateVideo('next')}
                        >
                          <ChevronRight className="w-8 h-8" />
                        </Button>
                      </>
                    )}
                  </div>
                  
                  {/* Close Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-4 right-4 bg-black/60 hover:bg-black/80 text-white border-0"
                    onClick={() => setVideoViewerOpen(false)}
                  >
                    <X className="w-5 h-5" />
                  </Button>
                  
                  {/* Video Counter */}
                  {rdoData.videos.length > 1 && (
                    <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm">
                      {currentVideoIndex + 1} / {rdoData.videos.length}
                    </div>
                  )}
                </div>
                
                {/* Video Info */}
                <div className="bg-background border-t p-4 space-y-2">
                  <div className="text-center">
                    <h3 className="font-medium text-lg">
                      {rdoData.videos[currentVideoIndex]?.title || `Vídeo ${currentVideoIndex + 1}`}
                    </h3>
                    {rdoData.videos[currentVideoIndex]?.duration && (
                      <p className="text-sm text-muted-foreground">
                        Duração: {rdoData.videos[currentVideoIndex].duration}
                      </p>
                    )}
                  </div>
                  
                  {/* Video Thumbnails Navigation */}
                  {rdoData.videos.length > 1 && (
                    <div className="flex justify-center gap-2 mt-4 overflow-x-auto py-2">
                      {rdoData.videos.map((video: any, index: number) => (
                        <button
                          key={index}
                          onClick={() => setCurrentVideoIndex(index)}
                          className={`flex-shrink-0 w-20 h-12 bg-muted rounded border-2 transition-all duration-200 hover:scale-105 ${
                            index === currentVideoIndex 
                              ? 'border-primary shadow-lg' 
                              : 'border-transparent hover:border-primary/50'
                          }`}
                        >
                          <div className="w-full h-full flex items-center justify-center">
                            <FileVideo className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Caption Edit Modal */}
      <Dialog open={showCaptionEditModal} onOpenChange={setShowCaptionEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingCaptionType === 'photo' ? 'Editar Legenda da Foto' : 'Editar Título do Vídeo'}
            </DialogTitle>
            <DialogDescription>
              {editingCaptionType === 'photo' 
                ? 'Adicione ou edite a legenda desta foto.' 
                : 'Adicione ou edite o título deste vídeo.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="caption-edit">
                {editingCaptionType === 'photo' ? 'Legenda' : 'Título'}
              </Label>
              <Textarea
                id="caption-edit"
                value={editingCaptionText}
                onChange={(e) => setEditingCaptionText(e.target.value)}
                placeholder={editingCaptionType === 'photo' ? 'Digite a legenda da foto...' : 'Digite o título do vídeo...'}
                className="mt-1"
                rows={3}
              />
            </div>
            
            <div className="flex justify-between">
              <Button 
                variant="outline" 
                onClick={generateCaptionWithAI}
                className="flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                Sugerir com IA
              </Button>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => setShowCaptionEditModal(false)}
                >
                  Cancelar
                </Button>
                <Button onClick={saveCaptionEdit}>
                  Salvar
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
    </div>
  );
}

export default function PreenchendoRDOWrapper() {
  return (
    <ErrorBoundary>
      <PreenchendoRDO />
    </ErrorBoundary>
  );
}